/**
 * Screen-space selection (rect, lasso/polygon/brush masks, points).
 *
 * Runs on the CPU over the model's world-space centers, so results are exact
 * and identical on WebGL2 and WebGPU. Two modes, matching SuperSplat:
 *  - centers: a splat is hit when its projected center lies in the region.
 *  - footprint (> 0): a splat is hit when its projected 3-sigma ellipse,
 *    scaled by `footprint`, touches the region.
 * Depth-aware ("visible only") selection narrows these results with the GPU
 * pick buffer; see SplatEditSystem.
 */
import type { SplatEditModel } from '../SplatEditModel';

export interface ScreenContext {
    /** Column-major view-projection matrix (world → clip). */
    viewProj: ArrayLike<number>;
    /** Column-major view matrix (world → view). Needed for footprint mode. */
    view?: ArrayLike<number>;
    /** Column-major projection matrix. Needed for footprint mode. */
    proj?: ArrayLike<number>;
    /** Viewport size in pixels. Region coordinates are normalised to this. */
    width: number;
    height: number;
}

/** A region of the screen in normalised coordinates (0..1, origin top-left). */
export interface ScreenRegion {
    /** Normalised bounds used for early rejection. */
    minX: number; minY: number; maxX: number; maxY: number;
    /** Whether a normalised point is inside. */
    contains(nx: number, ny: number): boolean;
}

export function rectRegion(x1: number, y1: number, x2: number, y2: number): ScreenRegion {
    const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);
    return {
        minX, minY, maxX, maxY,
        contains: (nx, ny) => nx >= minX && nx <= maxX && ny >= minY && ny <= maxY,
    };
}

/**
 * Region from a coverage bitmap (e.g. a brush/lasso canvas). `alpha` holds one
 * byte per pixel (or RGBA with `stride` 4); coverage above `threshold` counts.
 */
export function maskRegion(
    alpha: ArrayLike<number>, width: number, height: number,
    options: { stride?: number; channel?: number; threshold?: number } = {},
): ScreenRegion {
    const stride = options.stride ?? 1;
    const channel = options.channel ?? 0;
    const threshold = options.threshold ?? 127;
    let minPx = width, minPy = height, maxPx = -1, maxPy = -1;
    for (let y = 0; y < height; y++) {
        const row = y * width;
        for (let x = 0; x < width; x++) {
            if (alpha[(row + x) * stride + channel] > threshold) {
                if (x < minPx) minPx = x;
                if (x > maxPx) maxPx = x;
                if (y < minPy) minPy = y;
                if (y > maxPy) maxPy = y;
            }
        }
    }
    if (maxPx < 0) {
        return { minX: 1, minY: 1, maxX: 0, maxY: 0, contains: () => false };
    }
    return {
        minX: minPx / width,
        minY: minPy / height,
        maxX: (maxPx + 1) / width,
        maxY: (maxPy + 1) / height,
        contains: (nx, ny) => {
            const px = Math.floor(nx * width);
            const py = Math.floor(ny * height);
            if (px < 0 || py < 0 || px >= width || py >= height) return false;
            return alpha[(py * width + px) * stride + channel] > threshold;
        },
    };
}

/** Polygon region (lasso / polygon tools) from normalised vertices. */
export function polygonRegion(points: { x: number; y: number }[]): ScreenRegion {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
    }
    const n = points.length;
    const xs = new Float64Array(n), ys = new Float64Array(n);
    points.forEach((p, i) => { xs[i] = p.x; ys[i] = p.y; });
    return {
        minX, minY, maxX, maxY,
        contains: (nx, ny) => {
            if (n < 3) return false;
            let inside = false;
            for (let i = 0, j = n - 1; i < n; j = i++) {
                if (((ys[i] > ny) !== (ys[j] > ny))
                    && (nx < (xs[j] - xs[i]) * (ny - ys[i]) / (ys[j] - ys[i]) + xs[i])) {
                    inside = !inside;
                }
            }
            return inside;
        },
    };
}

/** Disc region (single brush dab / point pick with tolerance), radius in pixels. */
export function circleRegion(nx: number, ny: number, radiusPx: number, width: number, height: number): ScreenRegion {
    const rx = radiusPx / width, ry = radiusPx / height;
    return {
        minX: nx - rx, maxX: nx + rx, minY: ny - ry, maxY: ny + ry,
        contains: (x, y) => {
            const dx = (x - nx) / rx, dy = (y - ny) / ry;
            return dx * dx + dy * dy <= 1;
        },
    };
}

/**
 * Hit-test every selectable splat against a screen region. Returns a mask
 * with 1 for hits. Hidden/deleted splats are never hit.
 */
export function selectScreenRegion(
    model: SplatEditModel,
    ctx: ScreenContext,
    region: ScreenRegion,
    footprint = 0,
): Uint8Array {
    const n = model.numSplats;
    const result = new Uint8Array(n);
    if (region.maxX < region.minX || region.maxY < region.minY) return result;

    const centers = model.getWorldCenters();
    const m = ctx.viewProj;
    const m0 = m[0], m1 = m[1], m3 = m[3], m4 = m[4], m5 = m[5], m7 = m[7];
    const m8 = m[8], m9 = m[9], m11 = m[11], m12 = m[12], m13 = m[13], m15 = m[15];
    const state = model.state;

    const useFootprint = footprint > 0 && ctx.proj && ctx.view;
    const radii = useFootprint ? model.getWorldRadii() : null;
    // pixels per world unit at clip w = 1
    const focalY = useFootprint ? (ctx.proj![5] * ctx.height) / 2 : 0;
    const focalX = useFootprint ? (ctx.proj![0] * ctx.width) / 2 : 0;
    const width = ctx.width, height = ctx.height;
    // perspective projections have a zero bottom-right element (w = -z_view)
    const perspective = useFootprint ? ctx.proj![15] === 0 : false;
    const footprintEllipse = useFootprint
        ? new FootprintEllipse(model, ctx.view!, ctx.proj!, width, height)
        : null;

    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        const cx = centers[i * 3], cy = centers[i * 3 + 1], cz = centers[i * 3 + 2];
        const w = m3 * cx + m7 * cy + m11 * cz + m15;
        if (w <= 0) continue; // behind the camera
        const ndcX = (m0 * cx + m4 * cy + m8 * cz + m12) / w;
        const ndcY = (m1 * cx + m5 * cy + m9 * cz + m13) / w;
        const sx = ndcX * 0.5 + 0.5;
        const sy = 0.5 - ndcY * 0.5;

        if (!useFootprint) {
            if (sx < region.minX || sx > region.maxX || sy < region.minY || sy > region.maxY) continue;
            if (region.contains(sx, sy)) result[i] = 1;
            continue;
        }

        // footprint: cheap circular bound first. Off-axis, perspective widens the
        // projected ellipse by up to sqrt(1 + (x/z)² + (y/z)²) (the view-space
        // slope of the center), so scale the bound by that or splats near the
        // screen edges are rejected before their ellipse is tested.
        let stretch = 1;
        if (perspective) {
            const slopeX = (ndcX + ctx.proj![8]) / ctx.proj![0];
            const slopeY = (ndcY + ctx.proj![9]) / ctx.proj![5];
            stretch = Math.sqrt(1 + slopeX * slopeX + slopeY * slopeY);
        }
        const rPx = radii![i] * footprint * Math.max(focalX, focalY) * stretch / w;
        const rx = rPx / width, ry = rPx / height;
        if (sx + rx < region.minX || sx - rx > region.maxX || sy + ry < region.minY || sy - ry > region.maxY) continue;
        if (sx >= region.minX && sx <= region.maxX && sy >= region.minY && sy <= region.maxY
            && region.contains(sx, sy)) {
            result[i] = 1;
            continue;
        }
        if (footprintEllipse!.touches(i, sx, sy, footprint, region)) result[i] = 1;
    }
    return result;
}

/**
 * Projected ellipse of a splat, used by footprint mode when the center itself
 * is outside the region. Samples the ellipse boundary and interior against the
 * region, which is exact enough for rect/lasso/brush regions at screen scale.
 */
class FootprintEllipse {
    private readonly model: SplatEditModel;
    private readonly view: ArrayLike<number>;
    private readonly fx: number;
    private readonly fy: number;
    private readonly ortho: boolean;
    private readonly width: number;
    private readonly height: number;
    private readonly combined: Float32Array;

    constructor(model: SplatEditModel, view: ArrayLike<number>, proj: ArrayLike<number>, width: number, height: number) {
        this.model = model;
        this.view = view;
        this.fx = proj[0] * width / 2;
        this.fy = proj[5] * height / 2;
        // orthographic projections keep w = 1 (bottom-right element 1)
        this.ortho = proj[15] !== 0;
        this.width = width;
        this.height = height;
        this.combined = model.combinedTransforms();
    }

    touches(i: number, sx: number, sy: number, footprint: number, region: ScreenRegion): boolean {
        const axes = this.axes(i);
        if (!axes) return false;
        const [a1x, a1y, a2x, a2y] = axes;
        const k = footprint;
        const w = this.width, h = this.height;
        // 16 boundary samples + 8 at half radius
        for (let s = 0; s < 24; s++) {
            const ring = s < 16 ? 1 : 0.5;
            const t = (s < 16 ? s / 16 : (s - 16) / 8) * Math.PI * 2;
            const c = Math.cos(t) * ring * k, d = Math.sin(t) * ring * k;
            const px = sx + (a1x * c + a2x * d) / w;
            const py = sy + (a1y * c + a2y * d) / h;
            if (px >= region.minX && px <= region.maxX && py >= region.minY && py <= region.maxY
                && region.contains(px, py)) {
                return true;
            }
        }
        return false;
    }

    /** Screen-space ellipse semi-axes in pixels (3 sigma), y down. */
    private axes(i: number): [number, number, number, number] | null {
        const model = this.model;
        const o = model.transformIndex[i] * 12;
        const M = this.combined; // world*palette, 3x4 row-major
        const v = this.view;

        // rotation (w, x, y, z) and scale
        const qw = model.col('rot_0')[i], qx = model.col('rot_1')[i];
        const qy = model.col('rot_2')[i], qz = model.col('rot_3')[i];
        const ql = Math.hypot(qw, qx, qy, qz) || 1;
        const w = qw / ql, x = qx / ql, y = qy / ql, z = qz / ql;
        const sx = Math.exp(model.col('scale_0')[i]);
        const sy = Math.exp(model.col('scale_1')[i]);
        const sz = Math.exp(model.col('scale_2')[i]);

        // local rotation matrix columns scaled
        const r00 = 1 - 2 * (y * y + z * z), r01 = 2 * (x * y - w * z), r02 = 2 * (x * z + w * y);
        const r10 = 2 * (x * y + w * z), r11 = 1 - 2 * (x * x + z * z), r12 = 2 * (y * z - w * x);
        const r20 = 2 * (x * z - w * y), r21 = 2 * (y * z + w * x), r22 = 1 - 2 * (x * x + y * y);
        // L = R * S (columns scaled)
        const L = [r00 * sx, r01 * sy, r02 * sz, r10 * sx, r11 * sy, r12 * sz, r20 * sx, r21 * sy, r22 * sz];

        // world-from-local linear part (3x3 row-major from combined)
        const A = [M[o], M[o + 1], M[o + 2], M[o + 4], M[o + 5], M[o + 6], M[o + 8], M[o + 9], M[o + 10]];
        // view linear part (row-major from column-major view)
        const V = [v[0], v[4], v[8], v[1], v[5], v[9], v[2], v[6], v[10]];
        const VA = mul3(V, A);
        const T = mul3(VA, L); // view-space axes (columns)

        // view-space center
        const centers = model.getWorldCenters();
        const cx = centers[i * 3], cy = centers[i * 3 + 1], cz = centers[i * 3 + 2];
        const tx = v[0] * cx + v[4] * cy + v[8] * cz + v[12];
        const ty = v[1] * cx + v[5] * cy + v[9] * cz + v[13];
        const tz = v[2] * cx + v[6] * cy + v[10] * cz + v[14];
        const depth = -tz;
        if (!this.ortho && depth <= 1e-6) return null;

        // Jacobian of the projection (pixels), y flipped for screen. Orthographic
        // projection is linear: no division by depth (dividing made every ortho
        // footprint far too small to touch the region).
        const j00 = this.ortho ? this.fx : this.fx / depth;
        const j02 = this.ortho ? 0 : this.fx * tx / (depth * depth);
        const j11 = this.ortho ? -this.fy : -this.fy / depth;
        const j12 = this.ortho ? 0 : -this.fy * ty / (depth * depth);
        // J * T  (2x3)
        const p0 = [j00 * T[0] + j02 * T[6], j00 * T[1] + j02 * T[7], j00 * T[2] + j02 * T[8]];
        const p1 = [j11 * T[3] + j12 * T[6], j11 * T[4] + j12 * T[7], j11 * T[5] + j12 * T[8]];
        // covariance 2x2 = P P^T
        const a = p0[0] * p0[0] + p0[1] * p0[1] + p0[2] * p0[2];
        const b = p0[0] * p1[0] + p0[1] * p1[1] + p0[2] * p1[2];
        const c = p1[0] * p1[0] + p1[1] * p1[1] + p1[2] * p1[2];
        // eigen decomposition
        const mid = 0.5 * (a + c);
        const rad = Math.sqrt(Math.max(0, 0.25 * (a - c) * (a - c) + b * b));
        const l1 = mid + rad, l2 = Math.max(0, mid - rad);
        let ex = b, ey = l1 - a;
        if (Math.abs(ex) + Math.abs(ey) < 1e-12) { ex = 1; ey = 0; }
        const el = Math.hypot(ex, ey);
        ex /= el; ey /= el;
        const s1 = 3 * Math.sqrt(l1), s2 = 3 * Math.sqrt(l2);
        return [ex * s1, ey * s1, -ey * s2, ex * s2];
    }
}

function mul3(a: number[], b: number[]): number[] {
    const r = new Array(9);
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            r[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j];
        }
    }
    return r;
}
